/**
 * ============================================================================
 * USER MANAGEMENT API SERVICE
 * ============================================================================
 *
 * This service provides user management functionality with a hybrid approach:
 * - Uses REAL backend API endpoints where available
 * - Uses MOCK extensions for features not yet implemented in backend
 *
 * REAL BACKEND ENDPOINTS (✅ Available):
 * - GET  /user       - Get all users
 * - GET  /user/:id   - Get user by ID
 * - POST /user       - Create user with Firebase
 *
 * MOCK EXTENSIONS (⚠️  Temporary):
 * - All other user management operations (invite, role change, deactivate, etc.)
 * - See users.service.mock-extensions.ts for implementation details
 *
 * Migration Path:
 * As backend endpoints become available, replace the mock queryFn implementations
 * with standard RTK Query configurations pointing to real endpoints.
 * ============================================================================
 */

import type { FireBaseUserSchemaVM } from 'src/models/users.model';
import { baseApi } from './baseApi.service';
import { ENDPOINT } from '../config/apiTags';
import type { UserVM, InviteCreate, InviteVM, AuditLogVM, AuditAction } from '@tto/shared-models';

// User Management Tags
const USER_MANAGEMENT_TAG = 'UserManagement' as const;
const INVITATION_TAG = 'Invitation' as const;
const AUDIT_LOG_TAG = 'AuditLog' as const;

const buildMockUser = (userId: string, input?: Partial<UserVM>): UserVM => ({
  id: userId,
  firstName: input?.firstName ?? 'Mock',
  lastName: input?.lastName ?? 'User',
  email: input?.email ?? `mock-${userId}@example.com`,
  role: input?.role ?? 'USER',
});


const buildMockAuditLogs = (userId: string): AuditLogVM[] => [
  {
    id: `audit-${Date.now()}`,
    action: 'USER_ROLE_CHANGED' as AuditAction,
    actorId: 'user-1',
    targetId: userId,
    targetEmail: `mock-${userId}@example.com`,
    details: { source: 'mock' },
    ipAddress: '127.0.0.1',
    userAgent: navigator.userAgent,
    createdAt: new Date(),
  },
];
const usersApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // ======================== EXISTING ENDPOINT ========================
    getLoggedInUser: builder.query<FireBaseUserSchemaVM, string>({
      query: (id) => ({
        url: `${ENDPOINT.USER}/${id}`,
        method: 'GET',
      }),
    }),

    // ======================== USER MANAGEMENT ENDPOINTS ========================

    /**
     * ✅ USES REAL BACKEND: GET /user
     * Get all users with filtering, search, and pagination
     *
     * Note: Backend returns all users without filtering/pagination.
     * Client-side filtering is applied here until backend supports these features.
     */
    getUsersWithFilters: builder.query<UserVM[], void>({
      query: () => ({
        url: ENDPOINT.USER,
        method: 'GET',
      }),
    }),

    /**
     * ✅ USES REAL BACKEND: GET /user/:id
     * Get user by ID
     */
    getUserById: builder.query<UserVM, string>({
      query: (userId) => ({
        url: `${ENDPOINT.USER}/${userId}`,
        method: 'GET',
      }),
      providesTags: (result, error, userId) => [{ type: USER_MANAGEMENT_TAG, id: userId }],
    }),

    /**
     * ✅ USES REAL BACKEND: POST /user/invite (not implemented in backend)
     * Invite new user
     */
    inviteUser: builder.mutation<InviteVM, InviteCreate>({
      query: (input) => ({
        url: `${ENDPOINT.USER}/invite`,
        method: 'POST',
        body: input,
      }),
      invalidatesTags: [
        { type: USER_MANAGEMENT_TAG, id: 'LIST' },
        { type: INVITATION_TAG, id: 'LIST' },
      ],
    }),

    /**
     * ⚠️  USES MOCK: PATCH /users/:id/role (not implemented in backend)
     * Update user role
     * TODO: Replace with real backend endpoint when available
     */
    updateUserRole: builder.mutation<UserVM, { userId: string; input: Partial<UserVM> }>({
      queryFn: async ({ userId, input }) => {
        try {
          console.log('Updating user role with mock API', { userId, input });
          return { data: buildMockUser(userId, input) };
        } catch (error) {
          return {
            error: {
              status: error instanceof Error && error.message === 'User not found' ? 404 : 400,
              data: error instanceof Error ? error.message : 'Failed to update user role',
            },
          };
        }
      },
      invalidatesTags: (result, error, { userId }) => [
        { type: USER_MANAGEMENT_TAG, id: userId },
        { type: USER_MANAGEMENT_TAG, id: 'LIST' },
        { type: AUDIT_LOG_TAG, id: userId },
      ],
    }),

    /**
     * ⚠️  USES MOCK: PATCH /users/:id/deactivate (not implemented in backend)
     * Deactivate user
     * TODO: Replace with real backend endpoint when available
     */
    deactivateUser: builder.mutation<UserVM, { userId: string; input: Partial<UserVM> }>({
      queryFn: async ({ userId, input }) => {
        try {
          console.log('Deactivating user with mock API', { userId, input });
          return { data: buildMockUser(userId, input) };
        } catch (error) {
          return {
            error: {
              status: error instanceof Error && error.message === 'User not found' ? 404 : 400,
              data: error instanceof Error ? error.message : 'Failed to deactivate user',
            },
          };
        }
      },
      invalidatesTags: (result, error, { userId }) => [
        { type: USER_MANAGEMENT_TAG, id: userId },
        { type: USER_MANAGEMENT_TAG, id: 'LIST' },
        { type: AUDIT_LOG_TAG, id: userId },
      ],
    }),

    /**
     * ⚠️  USES MOCK: PATCH /users/:id/reinstate (not implemented in backend)
     * Reinstate user
     * TODO: Replace with real backend endpoint when available
     */
    reinstateUser: builder.mutation<UserVM, { userId: string; input: Partial<UserVM> }>({
      queryFn: async ({ userId, input }) => {
        try {
          console.log('Reinstating user with mock API', { userId, input });
          return { data: buildMockUser(userId, input) };
        } catch (error) {
          return {
            error: {
              status: error instanceof Error && error.message === 'User not found' ? 404 : 400,
              data: error instanceof Error ? error.message : 'Failed to reinstate user',
            },
          };
        }
      },
      invalidatesTags: (result, error, { userId }) => [
        { type: USER_MANAGEMENT_TAG, id: userId },
        { type: USER_MANAGEMENT_TAG, id: 'LIST' },
        { type: AUDIT_LOG_TAG, id: userId },
      ],
    }),

    /**
     * ⚠️  USES MOCK: DELETE /users/:id (not implemented in backend)
     * Delete user permanently
     * TODO: Replace with real backend endpoint when available
     */
    deleteUser: builder.mutation<void, { userId: string; input: Partial<UserVM> }>({
      queryFn: async ({ userId, input }) => {
        try {
          console.log('Deleting user with mock API', { userId, input });
          return { data: undefined };
        } catch (error) {
          return {
            error: {
              status: error instanceof Error && error.message === 'User not found' ? 404 : 400,
              data: error instanceof Error ? error.message : 'Failed to delete user',
            },
          };
        }
      },
      invalidatesTags: (result, error, { userId }) => [
        { type: USER_MANAGEMENT_TAG, id: userId },
        { type: USER_MANAGEMENT_TAG, id: 'LIST' },
      ],
    }),

    /**
     * ⚠️  USES MOCK: GET /users/:id/audit-logs (not implemented in backend)
     * Get audit logs for a user
     * TODO: Replace with real backend endpoint when available
     */
    getUserAuditLogs: builder.query<AuditLogVM[], string>({
      queryFn: async (userId) => {
        try {
          console.log('Fetching audit logs with mock API for userId', userId);
          return { data: buildMockAuditLogs(userId) };
        } catch (error) {
          return {
            error: {
              status: 500,
              data: error instanceof Error ? error.message : 'Failed to get audit logs',
            },
          };
        }
      },
      providesTags: (result, error, userId) => [{ type: AUDIT_LOG_TAG, id: userId }],
    }),

    /**
     * ✅ USES REAL BACKEND: GET /user/invite
     * Get pending and expired invitations
     */
    getPendingInvitations: builder.query<InviteVM[], void>({
      query: () => ({
        url: `${ENDPOINT.USER}/invite`,
        method: 'GET',
      }),
      providesTags: [{ type: INVITATION_TAG, id: 'LIST' }],
    }),

    /**
     * ✅ USES REAL BACKEND: POST /user/invite/:id/resend
     * Resend invitation
     */
    resendInvitation: builder.mutation<InviteVM, string>({
      query: (invitationId) => ({
        url: `${ENDPOINT.USER}/invite/${invitationId}/resend`,
        method: 'POST',
      }),
      invalidatesTags: [{ type: INVITATION_TAG, id: 'LIST' }],
    }),
  }),
});

export const {
  // Existing
  useGetLoggedInUserQuery,
  useLazyGetLoggedInUserQuery,

  // User Management
  useGetUsersWithFiltersQuery,
  useLazyGetUsersWithFiltersQuery,
  useGetUserByIdQuery,
  useLazyGetUserByIdQuery,
  useInviteUserMutation,
  useUpdateUserRoleMutation,
  useDeactivateUserMutation,
  useReinstateUserMutation,
  useDeleteUserMutation,
  useGetUserAuditLogsQuery,
  useLazyGetUserAuditLogsQuery,

  // Invitations
  useGetPendingInvitationsQuery,
  useResendInvitationMutation,
} = usersApi;
